/**
 * Capital Gains Tax Calculator — AXIORA (Feb 2026)
 *
 * Route: /tax/capital-gains
 * Sub-flows:
 *   1. Pick an Assessment Year (default: current)
 *   2. Upload broker Excel/CSV → Gemini extracts transactions → user reviews
 *      → commits into the transaction store
 *   3. Add / edit / delete individual transactions
 *   4. Live tax summary with 112A ₹1.25L exemption, indexation choice etc.
 *   5. One-click push to an existing Income-Tax Computation
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileSpreadsheet, Upload, Trash2, Plus, Pencil, Send, Sparkles,
  ArrowLeft, X, Check, AlertTriangle, RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/** Static asset options — mirrors ASSET_LABELS in capital_gains.py. */
const ASSET_OPTIONS = [
  { value: "listed_equity",   label: "Listed Equity Shares" },
  { value: "equity_mf",       label: "Equity Mutual Fund" },
  { value: "debt_mf",         label: "Debt Mutual Fund" },
  { value: "unlisted_shares", label: "Unlisted Shares" },
  { value: "property",        label: "Immovable Property" },
  { value: "gold",            label: "Gold / Bullion" },
  { value: "bonds",           label: "Bonds & Debentures" },
  { value: "crypto",          label: "Crypto / VDA" },
  { value: "other",           label: "Other" },
];
const ASSET_LABEL = Object.fromEntries(ASSET_OPTIONS.map((a) => [a.value, a.label]));

const BUCKET_LABEL = {
  stcg_111a:          "STCG @ 20% (equity, u/s 111A)",
  stcg_slab:          "STCG at slab rate",
  ltcg_112a:          "LTCG @ 12.5% (equity, u/s 112A — ₹1.25L exempt)",
  ltcg_20_indexed:    "LTCG @ 20% with indexation",
  ltcg_12_5_no_index: "LTCG @ 12.5% without indexation",
  ltcg_10_bonds:      "LTCG @ 10% (bonds)",
  ltcg_12_5_other:    "LTCG @ 12.5% (other)",
  crypto_115BBH:      "Crypto / VDA @ 30% (u/s 115BBH)",
};

const currentAY = () => {
  const d = new Date();
  const fyStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${fyStart + 1}-${String((fyStart + 2) % 100).padStart(2, "0")}`;
};

const EMPTY_TXN = () => ({
  asset_class: "listed_equity",
  security_name: "",
  isin: "",
  quantity: 0,
  purchase_date: "",
  sale_date: "",
  purchase_value: 0,
  sale_value: 0,
  transfer_expenses: 0,
  improvement_expenses: 0,
  stt_paid: true,
  fmv_31_jan_2018: 0,
  use_indexation: null,
  notes: "",
});

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ay, setAy] = useState(currentAY());
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assetFilter, setAssetFilter] = useState("all");

  // Import flow state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const importPollRef = useRef(null);

  // Manual add/edit dialog
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnForm, setTxnForm] = useState(EMPTY_TXN());
  const [editingId, setEditingId] = useState(null);

  // Push-to-ITR dialog
  const [pushOpen, setPushOpen] = useState(false);
  const [computations, setComputations] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState("");

  const stopImportPoll = () => {
    if (importPollRef.current) { clearInterval(importPollRef.current); importPollRef.current = null; }
  };
  useEffect(() => stopImportPoll, []);

  const refresh = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const [txnResp, sumResp] = await Promise.all([
        api.get(`/companies/${activeId}/capital-gains/transactions?ay=${ay}`),
        api.get(`/companies/${activeId}/capital-gains/summary?ay=${ay}`),
      ]);
      setTransactions(txnResp.data || []);
      setSummary(sumResp.data);
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not load capital gains"));
    } finally {
      setLoading(false);
    }
  }, [activeId, ay]);

  useEffect(() => { refresh(); }, [refresh]);

  const filteredTxns = useMemo(
    () => (assetFilter === "all"
      ? transactions
      : transactions.filter((t) => t.asset_class === assetFilter)),
    [transactions, assetFilter],
  );

  // Match computed rows from summary back to their originals for display.
  const computedById = useMemo(() => {
    if (!summary?.rows) return {};
    const m = {};
    summary.rows.forEach((r) => { m[r.id] = r; });
    return m;
  }, [summary]);

  /* ─── Import flow ────────────────────────────────────────────────── */
  const pollImport = (jobId) => new Promise((resolve, reject) => {
    stopImportPoll();
    let ticks = 0;
    importPollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 200) { stopImportPoll(); return reject(new Error("Import timed out")); }
      try {
        const { data } = await api.get(`/companies/${activeId}/capital-gains/import/jobs/${jobId}`);
        setImportProgress({ message: data.message, pct: data.progress_pct });
        if (data.status === "ready") { stopImportPoll(); resolve({ ...data.result, job_id: jobId }); }
        else if (data.status === "failed") { stopImportPoll(); reject(data.error || { message: "Failed" }); }
      } catch (err) {
        if (err?.response?.status === 404) { stopImportPoll(); reject(new Error("Job disappeared")); }
      }
    }, 1500);
  });

  const startImport = async () => {
    if (!importFile) { toast.error("Choose a file"); return; }
    setImportBusy(true);
    setImportResult(null);
    setImportProgress({ message: "Uploading …", pct: 5 });
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data: startResp } = await api.post(
        `/companies/${activeId}/capital-gains/import/start?ay=${ay}`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      const result = await pollImport(startResp.job_id);
      setImportResult(result);
      toast.success(`Extracted ${result.row_count} transactions — review below`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e;
      toast.error(detail?.message || describeAxiosError(e, "Import failed"));
    } finally {
      setImportBusy(false);
      setImportProgress(null);
    }
  };

  const commitImport = async () => {
    if (!importResult?.job_id) return;
    try {
      const { data } = await api.post(
        `/companies/${activeId}/capital-gains/import/jobs/${importResult.job_id}/commit`,
      );
      toast.success(`Added ${data.inserted} transactions`);
      setImportOpen(false);
      setImportFile(null);
      setImportResult(null);
      refresh();
    } catch (e) {
      toast.error(describeAxiosError(e, "Commit failed"));
    }
  };

  /* ─── Manual CRUD ────────────────────────────────────────────────── */
  const openAdd = () => { setTxnForm(EMPTY_TXN()); setEditingId(null); setTxnOpen(true); };
  const openEdit = (t) => {
    setTxnForm({
      asset_class: t.asset_class, security_name: t.security_name || "",
      isin: t.isin || "", quantity: t.quantity || 0,
      purchase_date: t.purchase_date, sale_date: t.sale_date,
      purchase_value: t.purchase_value || 0, sale_value: t.sale_value || 0,
      transfer_expenses: t.transfer_expenses || 0,
      improvement_expenses: t.improvement_expenses || 0,
      stt_paid: !!t.stt_paid, fmv_31_jan_2018: t.fmv_31_jan_2018 || 0,
      use_indexation: t.use_indexation, notes: t.notes || "",
    });
    setEditingId(t.id);
    setTxnOpen(true);
  };
  const saveTxn = async () => {
    try {
      if (editingId) {
        await api.patch(`/companies/${activeId}/capital-gains/transactions/${editingId}`, txnForm);
        toast.success("Updated");
      } else {
        await api.post(`/companies/${activeId}/capital-gains/transactions`, { ...txnForm, ay });
        toast.success("Added");
      }
      setTxnOpen(false);
      refresh();
    } catch (e) {
      toast.error(describeAxiosError(e, "Save failed"));
    }
  };
  const deleteTxn = async (t) => {
    if (!window.confirm(`Delete ${t.security_name || t.asset_class}?`)) return;
    try {
      await api.delete(`/companies/${activeId}/capital-gains/transactions/${t.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(describeAxiosError(e, "Delete failed"));
    }
  };

  /* ─── Push to ITR ─────────────────────────────────────────────────── */
  const openPushDialog = async () => {
    try {
      const { data } = await api.get(`/companies/${activeId}/income-tax/computations`);
      const forAy = (data || []).filter((c) => c.ay === ay);
      setComputations(forAy);
      setSelectedCompId(forAy[0]?.id || "");
      setPushOpen(true);
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not load computations"));
    }
  };
  const doPush = async () => {
    if (!selectedCompId) return;
    try {
      const { data } = await api.post(
        `/companies/${activeId}/capital-gains/push-to-itr/${selectedCompId}?ay=${ay}`,
      );
      toast.success(
        `Pushed to computation: STCG-111A ₹${formatINR(data.patch.stcg_111a)} · ` +
        `LTCG-112A ₹${formatINR(data.patch.ltcg_112a)} · ` +
        `Slab ₹${formatINR(data.patch.stcg_normal)}`,
      );
      setPushOpen(false);
    } catch (e) {
      toast.error(describeAxiosError(e, "Push failed"));
    }
  };

  const totalTax = summary?.totals?.tax || 0;
  const totalGain = summary?.totals?.gain || 0;

  return (
    <>
      <PageHeader
        title="Capital Gains Tax Calculator"
        subtitle={`Assessment Year ${ay} · ${transactions.length} transactions`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => nav("/tax")} className="h-8 text-sm" data-testid="btn-back-tax">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <select
              value={ay}
              onChange={(e) => setAy(e.target.value)}
              className="h-8 text-sm rounded border border-gray-300 px-2"
              data-testid="select-ay"
            >
              {["2024-25", "2025-26", "2026-27", "2027-28"].map((a) =>
                <option key={a} value={a}>AY {a}</option>)}
            </select>
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="btn-import-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Import Excel/CSV
            </Button>
            <Button onClick={openAdd} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-add-txn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-4">
        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <div className="border border-blue-200 bg-blue-50 rounded p-4">
              <div className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">Total Taxable Gain</div>
              <div className="text-2xl font-bold text-blue-900 mt-1 tabular-nums" data-testid="kpi-total-gain">
                ₹ {formatINR(totalGain)}
              </div>
              <div className="text-[11px] text-blue-700 mt-1">Sum across all buckets after exemptions</div>
            </div>
            <div className="border border-red-200 bg-red-50 rounded p-4">
              <div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold">Estimated Tax</div>
              <div className="text-2xl font-bold text-red-900 mt-1 tabular-nums" data-testid="kpi-total-tax">
                ₹ {formatINR(totalTax)}
              </div>
              <div className="text-[11px] text-red-700 mt-1">Before cess &amp; surcharge · applied at ITR level</div>
            </div>
            <div className="border border-amber-200 bg-amber-50 rounded p-4">
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Slab-Rate Gains</div>
              <div className="text-2xl font-bold text-amber-900 mt-1 tabular-nums" data-testid="kpi-slab-gain">
                ₹ {formatINR(summary.totals.slab_taxable)}
              </div>
              <div className="text-[11px] text-amber-700 mt-1">Added to salary/business income for slab tax</div>
            </div>
            <div className="border border-emerald-200 bg-emerald-50 rounded p-4 flex flex-col justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Ready?</div>
                <div className="text-sm text-emerald-800 mt-1">
                  Push these figures to an Income-Tax Computation for AY {ay}.
                </div>
              </div>
              <Button onClick={openPushDialog} className="mt-2 h-8 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="btn-push-itr">
                <Send className="w-3.5 h-3.5 mr-1" /> Push to ITR
              </Button>
            </div>
          </div>
        )}

        {/* Buckets breakdown */}
        {summary && (
          <div className="border border-gray-200 rounded bg-white">
            <div className="px-4 py-2 border-b border-gray-100 text-xs font-semibold uppercase tracking-wider text-gray-700">
              Tax Breakdown by Bucket
            </div>
            <table className="tx-table text-xs" data-testid="buckets-table">
              <thead>
                <tr>
                  <th>Bucket</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Net Gain</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">Tax</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.buckets)
                  .filter(([, b]) => b.count > 0)
                  .map(([key, b]) => (
                    <tr key={key} data-testid={`bucket-row-${key}`}>
                      <td>
                        {BUCKET_LABEL[key] || b.label}
                        {key === "ltcg_112a" && b.gross_before_exemption > 0 && (
                          <div className="text-[10px] text-gray-500">
                            Gross ₹{formatINR(b.gross_before_exemption)} − exemption ₹{formatINR(b.exemption_applied)}
                          </div>
                        )}
                      </td>
                      <td className="text-right tabular-nums">{b.count}</td>
                      <td className="text-right tabular-nums font-mono">₹ {formatINR(b.gain)}</td>
                      <td className="text-right tabular-nums">{b.rate}%</td>
                      <td className="text-right tabular-nums font-mono font-semibold">₹ {formatINR(b.tax)}</td>
                    </tr>
                  ))}
                {Object.values(summary.buckets).every((b) => b.count === 0) && (
                  <tr><td colSpan={5} className="text-center text-gray-400 py-6">
                    No transactions yet — add one manually or import a broker Excel to get started.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Transactions table */}
        <div className="border border-gray-200 rounded bg-white">
          <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 flex-1">
              Transactions ({filteredTxns.length})
            </div>
            <select
              value={assetFilter}
              onChange={(e) => setAssetFilter(e.target.value)}
              className="h-7 text-xs rounded border border-gray-300 px-2"
              data-testid="select-asset-filter"
            >
              <option value="all">All asset classes</option>
              {ASSET_OPTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <Button variant="ghost" onClick={refresh} className="h-7 text-xs" data-testid="btn-refresh-cg">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="tx-table text-xs">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Security</th>
                  <th className="text-right">Purchase</th>
                  <th className="text-right">Sale</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Proceeds</th>
                  <th className="text-right">Type</th>
                  <th className="text-right">Gain</th>
                  <th className="text-right">Tax</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="text-center py-6 text-gray-400">Loading…</td></tr>
                )}
                {!loading && filteredTxns.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-6 text-gray-400">
                    No transactions for this AY yet.
                  </td></tr>
                )}
                {filteredTxns.map((t) => {
                  const c = computedById[t.id] || {};
                  return (
                    <tr key={t.id} data-testid={`txn-row-${t.id}`}>
                      <td>
                        <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-700">
                          {ASSET_LABEL[t.asset_class] || t.asset_class}
                        </span>
                      </td>
                      <td className="font-medium">{t.security_name || <span className="text-gray-400">—</span>}</td>
                      <td className="text-right font-mono text-[11px]">{t.purchase_date}</td>
                      <td className="text-right font-mono text-[11px]">{t.sale_date}</td>
                      <td className="text-right font-mono tabular-nums">₹ {formatINR(t.purchase_value)}</td>
                      <td className="text-right font-mono tabular-nums">₹ {formatINR(t.sale_value)}</td>
                      <td className="text-right">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          c.gain_type === "LTCG" ? "bg-emerald-100 text-emerald-800" :
                          c.gain_type === "STCG" ? "bg-amber-100 text-amber-800" :
                          "bg-purple-100 text-purple-800"
                        }`}>{c.gain_type || "—"}</span>
                      </td>
                      <td className={`text-right font-mono tabular-nums ${(c.taxable_gain || 0) >= 0 ? "" : "text-red-600"}`}>
                        ₹ {formatINR(c.taxable_gain)}
                      </td>
                      <td className="text-right font-mono tabular-nums font-semibold">₹ {formatINR(c.tax_amount)}</td>
                      <td>
                        <div className="inline-flex gap-1">
                          <button onClick={() => openEdit(t)} className="text-gray-500 hover:text-blue-700" data-testid={`btn-edit-${t.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => deleteTxn(t)} className="text-gray-500 hover:text-red-700" data-testid={`btn-delete-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Import dialog */}
      <Dialog
        open={importOpen}
        onOpenChange={(o) => { if (!o) { setImportOpen(false); setImportFile(null); setImportResult(null); } }}
      >
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="import-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-600" />
              Import Broker Statement (AY {ay})
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Works with Zerodha · Motilal Oswal · Groww · Angel One · HDFC · ICICI Direct · custom sheets.
              AI (Gemini 3 Pro) auto-detects columns — no template setup required.
            </p>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
            {importBusy && importProgress && (
              <div className="border border-emerald-200 bg-emerald-50 rounded p-3" data-testid="import-progress">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 text-sm text-emerald-900 font-medium">
                    <Sparkles className="w-4 h-4 animate-pulse" />
                    <span>{importProgress.message || "Working…"}</span>
                  </div>
                  <div className="text-xs font-mono text-emerald-700">{importProgress.pct || 0}%</div>
                </div>
                <div className="h-1.5 bg-white border border-emerald-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-600 transition-all duration-500 ease-out"
                       style={{ width: `${importProgress.pct || 0}%` }} />
                </div>
              </div>
            )}
            {!importBusy && !importResult && (
              <label className="flex items-center justify-center border-2 border-dashed border-emerald-300 bg-emerald-50/40 rounded-lg p-8 text-sm text-emerald-700 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50">
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  data-testid="input-import-file"
                />
                <div className="text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
                  <div className="font-medium">{importFile ? importFile.name : "Click to choose .xlsx / .xls / .csv"}</div>
                  <div className="text-[11px] text-gray-500 mt-1">≤ 10 MB · any broker template auto-detected</div>
                </div>
              </label>
            )}
            {!importBusy && importResult && (
              <>
                <div className="border border-emerald-200 bg-emerald-50/50 rounded p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-emerald-800">
                      {importResult.row_count} transactions extracted from{" "}
                      <span className="font-mono">{importResult.broker || "unknown"}</span>
                    </span>
                    <span className="text-emerald-700">Review below, then click Commit</span>
                  </div>
                </div>
                {(importResult.warnings || []).length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs text-amber-900 space-y-1" data-testid="import-warnings">
                    <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Please review:</div>
                    <ul className="list-disc pl-5">
                      {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                <div className="border border-gray-200 rounded max-h-96 overflow-y-auto">
                  <table className="tx-table text-[11px]">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr>
                        <th>Asset</th><th>Security</th>
                        <th className="text-right">Purchase</th><th className="text-right">Sale</th>
                        <th className="text-right">Cost</th><th className="text-right">Proceeds</th>
                        <th className="text-right">Gain</th><th className="text-right">Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(importResult.rows || []).map((r, i) => {
                        const c = r._computed || {};
                        return (
                          <tr key={i} data-testid={`preview-row-${i}`}>
                            <td>{ASSET_LABEL[r.asset_class] || r.asset_class}</td>
                            <td className="max-w-xs truncate">{r.security_name}</td>
                            <td className="text-right font-mono">{r.purchase_date}</td>
                            <td className="text-right font-mono">{r.sale_date}</td>
                            <td className="text-right font-mono">₹ {formatINR(r.purchase_value)}</td>
                            <td className="text-right font-mono">₹ {formatINR(r.sale_value)}</td>
                            <td className="text-right font-mono">₹ {formatINR(c.taxable_gain)}</td>
                            <td className="text-right font-mono font-semibold">₹ {formatINR(c.tax_amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white">
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportFile(null); setImportResult(null); }} data-testid="btn-import-cancel">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            {!importResult ? (
              <Button
                onClick={startImport}
                disabled={!importFile || importBusy}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="btn-import-extract"
              >
                {importBusy
                  ? <><Sparkles className="w-4 h-4 mr-1 animate-pulse" /> Extracting…</>
                  : <><Upload className="w-4 h-4 mr-1" /> Extract with AI</>}
              </Button>
            ) : (
              <Button onClick={commitImport} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-import-commit">
                <Check className="w-4 h-4 mr-1" /> Commit {importResult.row_count} to Ledger
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/edit transaction dialog */}
      <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="txn-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-lg">{editingId ? "Edit transaction" : "New transaction"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 px-6 py-5 overflow-y-auto flex-1">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Asset class</Label>
              <select
                value={txnForm.asset_class}
                onChange={(e) => setTxnForm({ ...txnForm, asset_class: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-gray-300 text-sm"
                data-testid="input-asset-class"
              >
                {ASSET_OPTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Security / description</Label>
              <Input value={txnForm.security_name} onChange={(e) => setTxnForm({ ...txnForm, security_name: e.target.value })} className="h-10" placeholder="RELIANCE / Mutual fund name" data-testid="input-security-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">ISIN (optional)</Label>
              <Input value={txnForm.isin} onChange={(e) => setTxnForm({ ...txnForm, isin: e.target.value })} className="h-10 font-mono text-sm" placeholder="INE002A01018" data-testid="input-isin" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Quantity</Label>
              <Input type="number" value={txnForm.quantity} onChange={(e) => setTxnForm({ ...txnForm, quantity: parseFloat(e.target.value || 0) })} className="h-10" data-testid="input-quantity" />
            </div>
            <div className="space-y-1.5"></div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Purchase date *</Label>
              <Input type="date" value={txnForm.purchase_date} onChange={(e) => setTxnForm({ ...txnForm, purchase_date: e.target.value })} className="h-10" data-testid="input-purchase-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Sale date *</Label>
              <Input type="date" value={txnForm.sale_date} onChange={(e) => setTxnForm({ ...txnForm, sale_date: e.target.value })} className="h-10" data-testid="input-sale-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Purchase value (total ₹) *</Label>
              <Input type="number" value={txnForm.purchase_value} onChange={(e) => setTxnForm({ ...txnForm, purchase_value: parseFloat(e.target.value || 0) })} className="h-10" data-testid="input-purchase-value" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Sale value (total ₹) *</Label>
              <Input type="number" value={txnForm.sale_value} onChange={(e) => setTxnForm({ ...txnForm, sale_value: parseFloat(e.target.value || 0) })} className="h-10" data-testid="input-sale-value" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Transfer expenses (brokerage / STT)</Label>
              <Input type="number" value={txnForm.transfer_expenses} onChange={(e) => setTxnForm({ ...txnForm, transfer_expenses: parseFloat(e.target.value || 0) })} className="h-10" data-testid="input-transfer-expenses" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Improvement expenses (property only)</Label>
              <Input type="number" value={txnForm.improvement_expenses} onChange={(e) => setTxnForm({ ...txnForm, improvement_expenses: parseFloat(e.target.value || 0) })} className="h-10" data-testid="input-improvement" />
            </div>
            {(txnForm.asset_class === "listed_equity" || txnForm.asset_class === "equity_mf") && (
              <>
                <div className="col-span-2 flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    id="stt-paid"
                    checked={!!txnForm.stt_paid}
                    onChange={(e) => setTxnForm({ ...txnForm, stt_paid: e.target.checked })}
                    data-testid="input-stt-paid"
                  />
                  <label htmlFor="stt-paid" className="text-sm text-gray-700">
                    STT was paid on both buy and sell (mandatory for 111A / 112A rates)
                  </label>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">
                    FMV on 31-Jan-2018 (Section 55(2)(ac) grandfathering)
                  </Label>
                  <Input
                    type="number"
                    value={txnForm.fmv_31_jan_2018}
                    onChange={(e) => setTxnForm({ ...txnForm, fmv_31_jan_2018: parseFloat(e.target.value || 0) })}
                    className="h-10"
                    placeholder="Only for shares bought before 31-Jan-2018"
                    data-testid="input-fmv-2018"
                  />
                </div>
              </>
            )}
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Notes</Label>
              <Input value={txnForm.notes} onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })} className="h-10" data-testid="input-notes" />
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white">
            <Button variant="outline" onClick={() => setTxnOpen(false)}>Cancel</Button>
            <Button
              onClick={saveTxn}
              disabled={!txnForm.purchase_date || !txnForm.sale_date}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="btn-save-txn"
            >
              {editingId ? "Save changes" : "Add transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push-to-ITR dialog */}
      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent className="max-w-lg p-0 flex flex-col" data-testid="push-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-lg">Push to Income-Tax Computation</DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Fold this AY&apos;s capital-gains buckets into an existing computation.
              Only the four capital-gain heads (111A / 112A / STCG-slab / LTCG-other) are updated.
            </p>
          </DialogHeader>
          <div className="px-6 py-5 space-y-3">
            {computations.length === 0 ? (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                No computation exists for AY {ay} yet.{" "}
                <button onClick={() => nav("/tax/computation/new")} className="underline">
                  Create one first.
                </button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Target computation</Label>
                <select
                  value={selectedCompId}
                  onChange={(e) => setSelectedCompId(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 text-sm"
                  data-testid="select-target-comp"
                >
                  {computations.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.id.slice(0, 8)} · {c.status}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-gray-100">
            <Button variant="outline" onClick={() => setPushOpen(false)}>Cancel</Button>
            <Button
              onClick={doPush}
              disabled={!selectedCompId}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="btn-do-push"
            >
              <Send className="w-4 h-4 mr-1" /> Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CapitalGains() {
  return <RequireCompany><Inner /></RequireCompany>;
}
